import { Route, Navigate, Routes } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import EventEmitter, { Events } from "./services/eventemitter";
import Loader from "./components/Loader";
import ExtendedRouter from "./extendedRouter";
import history from "./history";
import MainLayout from "./Layout/MainLayout";
import { storageService } from "./services/StorageService";
import { httpService } from "./services/HttpService";
import { authService } from "./services/authService";
import AllTransactions from "./Pages/Operations/AllTransactions";
import IncomingIbanTransfers from "./Pages/Operations/IncomingIbanTransfers";
import IncomingCardDeposits from "./Pages/Operations/IncomingCardDeposits";
import InternalTransaction from "./Pages/Operations/InternalTransaction";
import OutgoingBGTransactions from "./Pages/Operations/OutgoingBGTransactions";
import OutgoingSEPATransactions from "./Pages/Operations/OutgoingSEPATransactions";
import OutgoingSwiftTransactions from "./Pages/Operations/OutgoingSwiftTransactions";
import SFTPTransactions from "./Pages/Operations/SFTPTransactions";
import Operations from "./Pages/Operations/Transactions";
import { getUserPermittedRoutes, isOperationOfficer, isRestrictedToLeadsView, isSuperAdmin, updatePermissions, updateRoles } from "./authGuard";
import OwnProfileComponent from "./Pages/Users/OperatorProfileOwn";

const LazyExecute = lazy(() => import("./components/Execute"));
const LazyAuthResult = lazy(() => import("./Pages/Auth/AuthResult"));
const LazyLogout = lazy(() => import("./Pages/Auth/Logout"));

interface LoginState {
    isLoggedIn: boolean;
}

class Router extends React.Component<any, LoginState> {
    constructor(props: any) {
        super(props);

        this.state = {
            // Not "is there a token" - "is there a session that has not expired". The former
            // rendered the whole app on a dead session, which is how a failed logout left an
            // operator still reading data (TNPDEVAPPS-1868).
            isLoggedIn: authService.isAuthenticated(),
        };

        EventEmitter.on(Events.onLogin, this.OnLogin);
        EventEmitter.on(Events.onLogout, this.onLogout);
    }

    OnLogin = async (token: string) => {
        this.setState({ isLoggedIn: true });
        storageService.setAccessToken(token);
        httpService.setAuthToken(token);

        try {
            // Independent calls, so run them together rather than making the second wait on the
            // first. Halves the time spent on this screen without changing what happens on failure.
            await Promise.all([updatePermissions(), updateRoles()]);
        } catch (error) {
            // Either call failing used to skip the navigation below, leaving the user signed in
            // but stranded on /auth-result - a route that exists only in the signed-out tree, so
            // the signed-in tree matches nothing and renders a blank page. Reloading could not
            // recover it either, because AuthResult is never mounted again to re-fire this event.
            // Navigating anyway lands them on a real page, where the worst case is a thinner menu
            // until the next sign-in refreshes the lists (TNPDEVAPPS-1868).
            console.error("Failed to load permissions or roles after login", error);
        }

        // Go to root page. necessary for router to take effect.
        window.location.href = "/";
    };

    onLogout = () => {
        this.setState({ isLoggedIn: false });
        storageService.clear();
        history.push("/");
    };

    render() {
        if (this.state.isLoggedIn === false) {
            return (
                <ExtendedRouter history={history}>
                    <Suspense fallback={<Loader />}>
                        <Routes>
                            <Route path="/login" element={<LazyExecute execute={() => httpService.login()} />} />
                            <Route path="/auth-result" element={<LazyAuthResult />} />
                            {/* Silent renew runs precisely when the token has expired, so
                                isAuthenticated() is false by definition and the hidden iframe
                                lands here rather than in the signed-in tree. Without this route
                                it would fall through to "*", redirect itself to /login and never
                                post a result back, so the renew would time out and the operator
                                would be bounced through a full login (TNPDEVAPPS-1868). */}
                            <Route path="/silentRenew" element={<LazyExecute execute={() => authService.signinSilentCallback()} />} />
                            <Route path="/logout" element={<LazyLogout />} />
                            <Route path="*" element={<Navigate to="/login" replace />} />
                        </Routes>
                    </Suspense>
                </ExtendedRouter>
            );
        }

        return (
            <ExtendedRouter history={history}>
                <Suspense fallback={<Loader />}>
                    <MainLayout>
                        <Routes>
                            {getUserPermittedRoutes().map((route) => {
                                return (
                                    <Route
                                        key={route.path}
                                        path={route.path}
                                        element={React.createElement(route.component, { ...route.componentProps })}
                                    />
                                );
                            })}
                            {(isSuperAdmin() || isOperationOfficer()) && (
                                <Route
                                    path="/all-transactions"
                                    element={
                                        <Operations>
                                            {" "}
                                            <AllTransactions />{" "}
                                        </Operations>
                                    }
                                />
                            )}
                            {/* <Route path="/outgoing-sepa-transactions" element={<Operations> <OutgoingSEPATransactions /> </Operations>} />
                                    <Route path="/outgoing-swift-transactions" element={<Operations> <OutgoingSwiftTransactions /> </Operations>} />
                                    <Route path="/outgoing-bg-transactions" element={<Operations> <OutgoingBGTransactions /> </Operations>} />
                                    <Route path="/internal-p2p-transactions" element={<Operations> <InternalTransaction /> </Operations>} />
                                    <Route path="/incoming-iban-transfers" element={<Operations> <IncomingIbanTransfers /> </Operations>} />
                                    <Route path="/incoming-card-deposits" element={<Operations> <IncomingCardDeposits /> </Operations>} /> */}
                            {isSuperAdmin() && (
                                <Route
                                    path="/sftp-files-exchanged"
                                    element={
                                        <Operations>
                                            {" "}
                                            <SFTPTransactions />{" "}
                                        </Operations>
                                    }
                                />
                            )}
                            <Route path="/" element={<Navigate to={isRestrictedToLeadsView() ? "/users" : "/dashboard"} replace />} />
                            <Route path="/profile" element={<OwnProfileComponent />} />
                            <Route path="/silentRenew" element={<LazyExecute execute={() => authService.signinSilentCallback()} />} />
                            <Route path="/logout" element={<LazyLogout />} />
                        </Routes>
                    </MainLayout>
                </Suspense>
            </ExtendedRouter>
        );
    }
}

export default Router;
